import { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { cn } from '../../lib/utils';

export function BackToTop({ 
  threshold = 300,
  position = 'bottom-right',
  offset = { bottom: 24, right: 24 }
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Show/hide button based on scroll position
      setIsVisible(window.scrollY > threshold);
      
      // Calculate scroll progress
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Position styles
  const positionStyles = {
    'bottom-right': { bottom: `${offset.bottom + 160}px`, right: `${offset.right}px` },
    'bottom-left': { bottom: `${offset.bottom + 160}px`, left: `${offset.right}px` },
  };

  return (
    <button
      onClick={scrollToTop}
      style={positionStyles[position]}
      className={cn(
        "fixed z-40 group",
        "transition-all duration-300 ease-out",
        isVisible 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-4 pointer-events-none"
      )}
      data-testid="back-to-top-button"
      aria-label="Scroll to top"
    >
      {/* Progress Ring */}
      <div className="relative">
        <svg 
          className="w-12 h-12 transform -rotate-90"
          viewBox="0 0 48 48"
        >
          {/* Background circle */}
          <circle
            cx="24"
            cy="24"
            r="20"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="3"
          />
          {/* Progress circle */}
          <circle
            cx="24"
            cy="24"
            r="20"
            fill="none"
            stroke="#FF6B35"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 20}`}
            strokeDashoffset={`${2 * Math.PI * 20 * (1 - scrollProgress / 100)}`}
            className="transition-all duration-150"
          />
        </svg>
        
        {/* Button content */}
        <div 
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "bg-white rounded-full shadow-lg m-1.5",
            "group-hover:bg-[#FF6B35] group-hover:shadow-xl",
            "transition-all duration-200"
          )}
        >
          <ArrowUp 
            className={cn(
              "w-5 h-5 text-[#004E64]",
              "group-hover:text-white group-hover:-translate-y-0.5",
              "transition-all duration-200"
            )}
          />
        </div>
      </div>
    </button>
  );
}
